import {
  Box,
  TextField,
  ButtonGroup,
  Button,
  InputLabel,
  Accordion,
  AccordionSummary,
  ToggleButton,
  ToggleButtonGroup,
  Fade,
  IconButton,
} from '@mui/material';
import { useState, useRef, useEffect } from 'react';
import { useRouteLoaderData } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import useTenantMutation from '@/hooks/useTenantMutation';
import { toast } from 'react-toastify';

export default function TenantRolesDetail({ data }) {
  const { schemas } = useRouteLoaderData('auth');
  const { mutateAsync: saveRole } = useTenantMutation('role', data?._id);

  const [expanded, setExpanded] = useState(false);
  const [roleData, setRoleData] = useState({
    name: data?.name,
    description: data?.description,
    superuser: data?.superuser || false,
    permissions: data?.permissions || {},
  });

  const scrollRef = useRef(null);
  const [showTopIndicator, setShowTopIndicator] = useState(false);
  const [showBottomIndicator, setShowBottomIndicator] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const checkScroll = () => {
      const atTop = el.scrollTop <= 2;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
      setShowTopIndicator(!atTop);
      setShowBottomIndicator(!atBottom);
    };

    checkScroll();
    el.addEventListener('scroll', checkScroll);
    return () => el.removeEventListener('scroll', checkScroll);
  }, [scrollRef.current]);

  const scrollTo = (direction) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({
      top: direction === 'top' ? 0 : scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setRoleData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    try {
      await saveRole(roleData);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Fehler beim Speichern der Rolle');
    }
  };

  const handleSuperuserToggle = (value) => {
    setRoleData((prevData) => ({
      ...prevData,
      superuser: value,
    }));
  };

  const handlePermissionChange = (schemaName) => (event, newPermissions) => {
    const permissions = {
      read: newPermissions.includes('READ'),
      create: newPermissions.includes('CREATE'),
      update: newPermissions.includes('UPDATE'),
      delete: newPermissions.includes('DELETE'),
    };

    setRoleData((prevData) => ({
      ...prevData,
      permissions: {
        ...prevData.permissions,
        [schemaName]: permissions,
      },
    }));
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        width: '100%',
        height: '100%',
      }}
    >
      <TextField
        fullWidth
        label="Name"
        name="name"
        value={roleData.name}
        variant="outlined"
        margin="normal"
        onChange={handleChange}
      />
      <TextField
        fullWidth
        label="Beschreibung"
        name="description"
        value={roleData.description}
        variant="outlined"
        margin="normal"
        onChange={handleChange}
      />
      <Box sx={{ mt: 2, mb: 2, width: '100%' }}>
        <InputLabel sx={{ mb: 1 }}>Superuser</InputLabel>
        <ButtonGroup fullWidth>
          <Button
            variant={roleData.superuser ? 'contained' : 'outlined'}
            onClick={() => handleSuperuserToggle(true)}
          >
            On
          </Button>
          <Button
            variant={!roleData.superuser ? 'contained' : 'outlined'}
            onClick={() => handleSuperuserToggle(false)}
          >
            Off
          </Button>
        </ButtonGroup>
      </Box>

      <Box
  sx={{
    position: 'relative',
    width: '100%',
    maxHeight: '70vh',
    mb: 2
  }}
></Box>
       <Box
    ref={scrollRef}
    sx={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: '100%',
      overflowY: 'auto',
      '&::-webkit-scrollbar': { display: 'none' },
      msOverflowStyle: 'none',
      scrollbarWidth: 'none',
    }}
  >
        {schemas &&
          Object.entries(schemas).map(([schemaName, schema]) => {
            const permissions = roleData?.permissions[schemaName] || {};
            const selectedPermissions = Object.entries(permissions)
              .filter(([_, value]) => value)
              .map(([key]) => key.toUpperCase());

            return (
              <Accordion
                key={schemaName}
                expanded={expanded === schemaName}
                onChange={handleAccordionChange(schemaName)}
                sx={{ width: '100%', mb: 2 }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`${schemaName}-content`}
                  id={`${schemaName}-header`}
                >
                  <InputLabel>
                    {schema.title || schemaName} ({schema.collection})
                  </InputLabel>
                </AccordionSummary>
                <Box sx={{ p: 2 }}>
                  <ToggleButtonGroup
                    value={selectedPermissions}
                    onChange={handlePermissionChange(schemaName)}
                    aria-label="Permissions"
                    size="small"
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      '& .MuiToggleButton-root': {
                        borderRadius: 1,
                        flex: '1 1 auto',
                        minWidth: 100,
                        fontWeight: 600,
                        color: 'text.primary',
                        backgroundColor: 'grey.100',
                        border: '1px solid transparent',
                        '&.Mui-selected': {
                          color: '#fff',
                          backgroundColor: 'primary.main',
                        },
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                          color: '#fff',
                        },
                      },
                    }}
                  >
                    {['READ', 'CREATE', 'UPDATE', 'DELETE'].map((permission) => (
                      <ToggleButton
                        key={permission}
                        value={permission}
                        aria-label={permission}
                      >
                        {permission}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
              </Accordion>
            );
          })}
      </Box>

      <Fade in={showTopIndicator}>
        <Box
          sx={{
            position: 'absolute',
            top: 260,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            backgroundColor: 'rgba(255,255,255,0.8)',
            borderRadius: '50%',
          }}
        >
          <IconButton onClick={() => scrollTo('top')}>
            <KeyboardArrowUpIcon />
          </IconButton>
        </Box>
      </Fade>
      <Fade in={showBottomIndicator}>
        <Box
          sx={{
            position: 'absolute',
            bottom: 60,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            backgroundColor: 'rgba(255,255,255,0.8)',
            borderRadius: '50%',
          }}
        >
          <IconButton onClick={() => scrollTo('bottom')}>
            <KeyboardArrowDownIcon />
          </IconButton>
        </Box>
      </Fade>

      <Button
        fullWidth
        variant="contained"
        onClick={handleSave}
        sx={{ mt: 2 }}
      >
        Speichern
      </Button>
    </Box>
  );
}
